/** Table definitions and the pure logic behind the tables: display, filter, sort, CSV. */

export type TableName = 'projects' | 'findings' | 'users';
export type ColumnType = 'text' | 'bool' | 'date' | 'url' | 'severity';
export type Row = Record<string, unknown>;

export interface Column {
  key: string;
  label: string;
  type?: ColumnType;
}

export const TABLES: Record<TableName, { title: string; columns: Column[] }> = {
  projects: {
    title: 'Projects',
    columns: [
      { key: 'workspace', label: 'Workspace' },
      { key: 'title', label: 'Project' },
      { key: 'projectUrl', label: 'Open in Replit', type: 'url' },
      { key: 'stale_public', label: 'Stale public', type: 'bool' },
      { key: 'hasDeployment', label: 'Deployed', type: 'bool' },
      { key: 'deploymentPrivacy', label: 'Privacy' },
      { key: 'deploymentStatus', label: 'Deploy status' },
      { key: 'deploymentUpdatedAt', label: 'Last published', type: 'date' },
      { key: 'deploymentUrl', label: 'Deployment URL', type: 'url' },
      { key: 'updatedAt', label: 'Project updated', type: 'date' },
      { key: 'creatorUsername', label: 'Creator' },
      { key: 'creatorEmail', label: 'Creator email' },
      { key: 'createdAt', label: 'Created', type: 'date' },
    ],
  },
  findings: {
    title: 'Findings',
    columns: [
      { key: 'severity', label: 'Severity', type: 'severity' },
      { key: 'code', label: 'Finding' },
      { key: 'subject', label: 'Subject' },
      { key: 'detail', label: 'Detail' },
      { key: 'workspace', label: 'Workspace' },
      { key: 'group', label: 'Group' },
    ],
  },
  users: {
    title: 'Users',
    columns: [
      { key: 'email', label: 'Email' },
      { key: 'displayName', label: 'Name' },
      { key: 'flags', label: 'Flags' },
      { key: 'replitProvisioned', label: 'In Replit', type: 'bool' },
      { key: 'entraFound', label: 'In Entra', type: 'bool' },
      { key: 'entraEnabled', label: 'Entra enabled', type: 'bool' },
      { key: 'replitIsAccountAdmin', label: 'Account admin', type: 'bool' },
      { key: 'replitWorkspaces', label: 'Workspaces (role)' },
      { key: 'entraGroups', label: 'Entra groups' },
      { key: 'replitLastSeen', label: 'Last seen', type: 'date' },
      { key: 'entraCreated', label: 'Entra created', type: 'date' },
      { key: 'notes', label: 'Notes' },
    ],
  },
};

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2, info: 3 };

/** The text a cell shows, filters on, and exports. */
export function cellText(value: unknown, type: ColumnType = 'text'): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (type === 'date' && typeof value === 'string') return value.slice(0, 10);
  return String(value);
}

/** Only http(s) values become links; anything else (javascript:, data:) renders as text. */
export function safeHref(value: unknown): string | undefined {
  return typeof value === 'string' && /^https?:\/\//i.test(value) ? value : undefined;
}

export function filterRows(rows: Row[], columns: Column[], search: string, perColumn: Record<string, string>): Row[] {
  const q = search.trim().toLowerCase();
  const filters = Object.entries(perColumn)
    .map(([key, v]) => [key, v.trim().toLowerCase()] as const)
    .filter(([, v]) => v);
  const types = Object.fromEntries(columns.map((c) => [c.key, c.type]));
  return rows.filter((row) => {
    for (const [key, v] of filters) {
      if (!cellText(row[key], types[key]).toLowerCase().includes(v)) return false;
    }
    if (!q) return true;
    return columns.some((c) => cellText(row[c.key], c.type).toLowerCase().includes(q));
  });
}

export function sortRows(rows: Row[], key: string, dir: 'asc' | 'desc', type: ColumnType = 'text'): Row[] {
  const sign = dir === 'asc' ? 1 : -1;
  const rank = (v: unknown): number | string => {
    if (type === 'severity') return SEVERITY_RANK[String(v)] ?? 9;
    if (typeof v === 'boolean') return v ? 1 : 0;
    return v === null || v === undefined ? '' : String(v).toLowerCase();
  };
  return [...rows].sort((a, b) => {
    const x = rank(a[key]);
    const y = rank(b[key]);
    if (x === y) return 0;
    // Blanks last in either direction: an empty "Last published" is not "oldest".
    if (x === '') return 1;
    if (y === '') return -1;
    return x < y ? -sign : sign;
  });
}

/**
 * RFC 4180 CSV of every field on the given rows (not just the shown columns).
 *
 * Cells starting with = + - @ (or tab/CR) are prefixed with ' so a spreadsheet treats them as text:
 * project titles and names are typed by any Replit user, and "=HYPERLINK(...)" would otherwise run.
 */
export function toCsv(rows: Row[], preferredOrder: string[] = []): string {
  const keys = [...preferredOrder];
  for (const row of rows) for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
  const cell = (v: unknown): string => {
    let s = cellText(v);
    if (typeof v !== 'boolean' && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [keys.map(cell).join(','), ...rows.map((r) => keys.map((k) => cell(r[k])).join(','))].join('\r\n') + '\r\n';
}

export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 (non-ASCII names) correctly.
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
